import { useEffect, useState } from "react";
import { api, ApiError } from "../api";
import { tg } from "../telegram";
import { useT } from "../i18n";
import type { InviteOut, UserRow } from "../types";

const TAB_KEYS = ["scada_v2", "auto", "combined", "incidents"] as const;
// Rights that are not panels, stored with them: «upload» = may send files / archives to the bot (off unless given).
const RIGHT_KEYS = ["upload"] as const;
const ALL_KEYS = [...TAB_KEYS, ...RIGHT_KEYS] as const;
type TabKey = typeof ALL_KEYS[number];

export default function Users({ embedded = false }: { embedded?: boolean } = {}) {
  const { t } = useT();
  const [users, setUsers] = useState<UserRow[] | null>(null);
  const [invite, setInvite] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [inviteTabs, setInviteTabs] = useState<Set<TabKey>>(new Set(TAB_KEYS));
  const [inviteName, setInviteName] = useState("");

  const tabLabel = (k: TabKey) =>
    k === "upload" ? `📥 ${t("users.right_upload")}`
      : k === "auto" ? `${t("tabs.telemetry")}: ${t("telemetry.src_pa")}`
      : k === "scada_v2" ? `${t("tabs.telemetry")}: G1 / G2`
        : k === "combined" ? t("tabs.combined")
          : t("tabs.incidents");

  const load = () =>
    api.get<UserRow[]>("/api/users").then(setUsers).catch((e: ApiError) => setError(e.message));
  useEffect(() => { load(); }, []);

  const chip = (on: boolean, label: string, onClick: () => void) => (
    <button className="btn secondary" onClick={onClick}
      style={{
        padding: "3px 9px", fontSize: 12, marginRight: 6,
        opacity: on ? 1 : 0.4,
        borderColor: on ? "var(--link, #2481cc)" : "var(--border)",
        color: on ? "var(--link, #2481cc)" : "var(--hint)",
      }}>
      {(on ? "☑ " : "☐ ") + label}
    </button>
  );

  const createInvite = async () => {
    setBusy(true); setError(null); setCopied(false);
    tg()?.HapticFeedback?.impactOccurred("light");
    try {
      const tabs = ALL_KEYS.filter((k) => inviteTabs.has(k));
      const r = await api.post<InviteOut>("/api/invites", {
        allowed_tabs: tabs.length ? tabs : TAB_KEYS,
        full_name: inviteName.trim() || null,
      });
      setInvite(r.link);
    } catch (e) {
      setError((e as ApiError).message);
    } finally {
      setBusy(false);
    }
  };

  const copy = async () => {
    if (!invite) return;
    try {
      await navigator.clipboard.writeText(invite);
      setCopied(true);
      tg()?.HapticFeedback?.notificationOccurred("success");
    } catch {
      /* clipboard blocked — the link is shown and selectable */
    }
  };

  const toggleActive = async (u: UserRow) => {
    tg()?.HapticFeedback?.impactOccurred("light");
    try {
      await api.post(`/api/users/${u.id}/toggle`, {});
      load();
    } catch (e) {
      setError((e as ApiError).message);
    }
  };

  const toggleUserTab = async (u: UserRow, k: TabKey) => {
    const cur = new Set((u.allowed_tabs || "").split(",").filter(Boolean));
    if (cur.has(k)) cur.delete(k); else cur.add(k);
    tg()?.HapticFeedback?.impactOccurred("light");
    try {
      await api.patch(`/api/users/${u.id}/tabs`, { allowed_tabs: ALL_KEYS.filter((x) => cur.has(x)) });
      load();
    } catch (e) {
      setError((e as ApiError).message);
    }
  };

  const removeUser = (u: UserRow) => {
    const name = u.full_name || String(u.telegram_id);
    const msg = t("users.delete_confirm").replace("{name}", name);
    const doDelete = async () => {
      tg()?.HapticFeedback?.notificationOccurred("warning");
      try { await api.delete(`/api/users/${u.id}`); load(); }
      catch (e) { setError((e as ApiError).message); }
    };
    const w = tg() as { showConfirm?: (m: string, cb: (ok: boolean) => void) => void } | undefined;
    if (w?.showConfirm) w.showConfirm(msg, (ok) => { if (ok) doDelete(); });
    else if (window.confirm(msg)) doDelete();
  };

  return (
    <div>
      {!embedded && <h1>{t("users.title")}</h1>}
      {error && <div className="alert error">{error}</div>}

      <div className="card" style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 12, color: "var(--hint)", marginBottom: 6 }}>{t("users.invite_name")}</div>
        <input value={inviteName} onChange={(e) => setInviteName(e.target.value)}
          placeholder={t("users.name_placeholder")}
          style={{
            width: "100%", boxSizing: "border-box", marginBottom: 12, padding: "8px 10px",
            border: "1px solid var(--border)", borderRadius: 8,
            background: "var(--bg)", color: "var(--text)", fontSize: 14,
          }} />
        <div style={{ fontSize: 12, color: "var(--hint)", marginBottom: 6 }}>{t("users.invite_tabs")}</div>
        <div style={{ marginBottom: 10 }}>
          {ALL_KEYS.map((k) =>
            <span key={k}>{chip(inviteTabs.has(k), tabLabel(k), () => setInviteTabs((prev) => {
              const next = new Set(prev);
              if (next.has(k)) next.delete(k); else next.add(k);
              return next;
            }))}</span>
          )}
        </div>
        <button className="btn" disabled={busy} onClick={createInvite}>{t("users.create_invite")}</button>
        <div style={{ fontSize: 12, color: "var(--hint)", marginTop: 8 }}>{t("users.invite_hint")}</div>
        {invite && (
          <div style={{ marginTop: 10 }}>
            <div style={{ fontSize: 12, color: "var(--hint)", marginBottom: 4 }}>{t("users.link_label")}</div>
            <div onClick={copy} style={{
              wordBreak: "break-all", background: "var(--bg)", border: "1px solid var(--border)",
              borderRadius: 8, padding: "8px 10px", fontSize: 13, cursor: "pointer",
            }}>{invite}</div>
            <button className="btn secondary" style={{ marginTop: 6, padding: "6px 10px" }} onClick={copy}>
              {copied ? "✅ " + t("users.copied") : "📋 " + t("users.copy")}
            </button>
          </div>
        )}
      </div>

      {!users && <div className="empty-state"><span className="spinner" /></div>}
      {users && users.length === 0 && <div className="empty-state">{t("users.none")}</div>}
      {users && users.length > 0 && (
        <div className="card" style={{ overflowX: "auto" }}>
          <table className="table" style={{ fontSize: 13 }}>
            <thead><tr>
              <th>{t("users.col_user")}</th><th>{t("users.col_status")}</th>
              <th>{t("users.col_tabs")}</th><th>{t("users.col_joined")}</th><th></th>
            </tr></thead>
            <tbody>
              {users.map(u => (
                <tr key={u.id}>
                  <td>
                    {u.full_name || "—"}
                    <div style={{ color: "var(--hint)", fontSize: 11 }}>{u.telegram_id}</div>
                  </td>
                  <td>{u.is_active ? "🟢 " + t("users.active") : "⚪️ " + t("users.inactive")}</td>
                  <td style={{ whiteSpace: "nowrap" }}>
                    {ALL_KEYS.map((k) => {
                      const on = (u.allowed_tabs || "").split(",").includes(k);
                      return <span key={k}>{chip(on, tabLabel(k), () => toggleUserTab(u, k))}</span>;
                    })}
                  </td>
                  <td style={{ fontSize: 11 }}>{u.created_at ? u.created_at.slice(0, 10) : ""}</td>
                  <td style={{ whiteSpace: "nowrap" }}>
                    <button className="btn secondary" style={{ padding: "4px 8px", fontSize: 12 }}
                      onClick={() => toggleActive(u)}>
                      {u.is_active ? t("users.deactivate") : t("users.activate")}
                    </button>
                    <button className="btn secondary"
                      style={{ padding: "4px 8px", fontSize: 12, marginLeft: 6, color: "#e3350d", borderColor: "#e3350d" }}
                      onClick={() => removeUser(u)}>
                      🗑 {t("users.delete")}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
